import React from "react";
import {
    PageHeader as DSPageHeader,
    PageStats,
    type PageStat,
} from "components/ui/page";
import { StatCard } from "components/ui/stat-card";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";
import type { Tone } from "components/ui/tone";

/* ══════════════════════════════════════════════════════════════════════════
   Compatibility layer over the design-system page anatomy.

   Eighteen files import this module and pass four raw colour props at every
   call site — `iconBg`, `iconColor`, `accentColor`, `accentHex`. That API is
   why Campaigns, Leads, Audiences and Templates each ended up with their own
   palette, their own KPI grid and their own idea of how big a page title is:
   nothing was shared except the wrapper.

   Rather than touch eighteen call sites at once, this file now translates the
   old colour props into the shared `tone` system and renders the new
   `PageHeader` / `StatCard`. Every page picks up the new typography, spacing,
   focus treatment and dark-mode handling without changing a line.

   New code should import `PageHeader`, `PageStats` and `StatCard` from
   `components/ui` and pass `tone` instead.
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Infers a tone from the legacy Tailwind colour strings.
 *
 * Matching is on hue family, so the mapping holds for every gradient in use
 * (`from-emerald-400 to-teal-500`, `from-violet-500 to-purple-500`, …) and
 * degrades to the brand tone for anything unrecognised.
 */
export function toneFromLegacyClasses(...classNames: Array<string | undefined>): Tone {
    const hay = classNames.filter(Boolean).join(" ").toLowerCase();
    if (/emerald|green|teal|lime/.test(hay)) return "success";
    if (/amber|yellow|orange/.test(hay)) return "warning";
    if (/red|rose|pink/.test(hay)) return "danger";
    if (/violet|purple|fuchsia/.test(hay)) return "violet";
    if (/gray|slate|zinc|neutral|stone/.test(hay)) return "neutral";
    if (/blue|indigo|sky|cyan/.test(hay)) return "brand";
    return "brand";
}

/* ── KPI card ─────────────────────────────────────────────────────────── */

export interface PageKPIItem {
    icon: React.ElementType;
    value: string | number | null | undefined;
    label: string;
    /** Legacy colour props — now only used to infer a tone */
    accentColor?: string;
    accentHex?: string;
    iconBg?: string;
    iconColor?: string;
    /** Preferred: set the tone directly */
    tone?: Tone;
    hint?: React.ReactNode;
    onClick?: () => void;
}

interface PageKPICardProps extends PageKPIItem {
    isLoading?: boolean;
}

export const PageKPICard: React.FC<PageKPICardProps> = ({
    icon,
    value,
    label,
    accentColor,
    iconBg,
    iconColor,
    tone,
    hint,
    onClick,
    isLoading = false,
}) => (
    <StatCard
        icon={icon}
        label={label}
        value={value as React.ReactNode}
        hint={hint}
        tone={tone ?? toneFromLegacyClasses(accentColor, iconBg, iconColor)}
        isLoading={isLoading}
        onClick={onClick}
    />
);

/* ── Page header ──────────────────────────────────────────────────────── */

export interface PageHeaderProps {
    /** Legacy API passes a rendered node, e.g. <Mail className="w-5 h-5" /> */
    icon: React.ReactNode;
    iconBg?: string;
    iconColor?: string;
    accentColor?: string;
    /** Preferred: set the tone directly */
    tone?: Tone;
    title: string;
    subtitle?: string;
    badge?: string | number | null;
    actions?: React.ReactNode;
    /** Slot before the icon — see the design-system PageHeader */
    leading?: React.ReactNode;
    kpis?: PageKPIItem[];
    isLoading?: boolean;
    className?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({
    icon,
    iconBg,
    iconColor,
    accentColor,
    tone,
    title,
    subtitle,
    badge,
    actions,
    leading,
    kpis,
    isLoading = false,
    className = "",
}) => {
    const resolvedTone = tone ?? toneFromLegacyClasses(accentColor, iconBg, iconColor);

    // The new header takes a component; the legacy API hands us an element.
    const IconComponent = React.useMemo(
        () => (icon ? () => <>{icon}</> : undefined),
        [icon]
    );

    const stats: PageStat[] = React.useMemo(
        () =>
            (kpis ?? []).map((kpi, i) => ({
                id: String(i),
                icon: kpi.icon,
                label: kpi.label,
                value: kpi.value as React.ReactNode,
                hint: kpi.hint,
                tone: kpi.tone ?? toneFromLegacyClasses(kpi.accentColor, kpi.iconBg, kpi.iconColor),
                onClick: kpi.onClick,
            })),
        [kpis]
    );

    /*
     * No bottom margin here: `Page` (and `PageContainer`, which now renders it)
     * supplies the rhythm between blocks. Keeping `mb-5` stacked a 20px margin
     * on top of the container's 20px gap, which is why the space under a page
     * header was twice the space anywhere else on the page.
     */
    return (
        <div className={cn("w-full max-w-full min-w-0 flex flex-col gap-4", className)}>
            <DSPageHeader
                icon={IconComponent}
                tone={resolvedTone}
                title={title}
                description={subtitle}
                badge={badge}
                actions={actions}
                leading={leading}
                isLoading={isLoading}
            />
            {stats.length > 0 && <PageStats stats={stats} isLoading={isLoading} />}
        </div>
    );
};

/* ── Buttons ──────────────────────────────────────────────────────────────
   Kept as named exports because ~16 headers import them. They now delegate to
   the shared Button, so a page action looks and focuses like every other
   button in the app. The signature Merko gradient is preserved on the primary
   action — it is part of the brand, not incidental styling.               */

export const PrimaryButton: React.FC<
    React.ButtonHTMLAttributes<HTMLButtonElement> & { icon?: React.ReactNode }
> = ({ children, icon, className = "", ...props }) => (
    <Button
        {...props}
        className={cn(
            "rounded-xl px-3.5 sm:px-4 py-2 min-h-[38px] text-ui font-semibold gap-1.5",
            "bg-[image:var(--brand-gradient)] hover:bg-[image:var(--brand-gradient-hover)]",
            "text-white shadow-xs hover:shadow-card-hover",
            "w-full sm:w-auto",
            className
        )}
    >
        {icon}
        {children}
    </Button>
);

export const SecondaryButton: React.FC<
    React.ButtonHTMLAttributes<HTMLButtonElement> & { icon?: React.ReactNode }
> = ({ children, icon, className = "", ...props }) => (
    <Button
        {...props}
        variant="secondary"
        className={cn(
            "rounded-xl px-3.5 sm:px-4 py-2 min-h-[38px] text-ui font-semibold gap-1.5",
            "w-full sm:w-auto",
            className
        )}
    >
        {icon}
        {children}
    </Button>
);

export default PageHeader;
